import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Zap,
  BrainCircuit,
  LineChart,
  Mail,
  Calendar,
  Users,
  TrendingUp,
  ArrowRight,
  Smartphone,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/networking.jpg";
import capture from "@/assets/nfc-card-tap.jpg";
import follow from "@/assets/ai-abstract.jpg";
import pipeline from "@/assets/cards-hero.webp";

export const Route = createFileRoute("/el/solutions/sales")({
  head: () => ({
    meta: [
      { title: "CRM & κάρτες NFC για ομάδες πωλήσεων — banana.cards" },
      { name: "description", content: "Καταγράψτε υποψήφιους πελάτες με ένα άγγιγμα, αφήστε το AI να συντάξει το follow-up και διατηρήστε το pipeline σας αξιόπιστο. Το Banana είναι η ολοκληρωμένη εργαλειοθήκη πωλήσεων για τον σύγχρονο επαγγελματία." },
      { property: "og:title", content: "CRM & κάρτες NFC για ομάδες πωλήσεων — banana.cards" },
      { property: "og:description", content: "Καταγράψτε υποψήφιους πελάτες με ένα άγγιγμα, αφήστε το AI να συντάξει το follow-up και διατηρήστε το pipeline σας αξιόπιστο." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/solutions/sales", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Για ομάδες πωλήσεων"
      title="Περισσότερες συμφωνίες,"
      italic="λιγότερη πληκτρολόγηση."
      description="Το Banana καταγράφει κάθε συζήτηση, συντάσσει το follow-up και συγχρονίζει το pipeline σας — ώστε η ομάδα σας να πουλάει, αντί να ενημερώνει πεδία στο CRM."
      heroImage={hero}
      metrics={[
        { v: "3×", l: "Ταχύτερο follow-up" },
        { v: "+38%", l: "Ποσοστό απάντησης έναντι τυποποιημένων μηνυμάτων πρώτης επαφής" },
        { v: "12 ώρες", l: "Εξοικονόμηση ανά πωλητή, την εβδομάδα" },
      ]}
      bullets={[
        { icon: Zap, title: "Καταγραφή υποψήφιου πελάτη σε 3 δευτερόλεπτα", desc: "Αγγίξτε, σαρώστε μια κάρτα ή ανεβάστε φωτογραφία επαγγελματικής κάρτας. Το Banana αναγνωρίζει την επαφή, την εμπλουτίζει με στοιχεία από το LinkedIn και την εταιρεία και την προσθέτει στο pipeline σας." },
        { icon: BrainCircuit, title: "Follow-up με AI, με τη δική σας φωνή", desc: "Βασισμένο στις σημειώσεις συναντήσεων, το ύφος και το προϊόν σας. Τα προσχέδια φτάνουν στα εισερχόμενά σας λίγα λεπτά μετά από κάθε κλήση." },
        { icon: LineChart, title: "Pipeline που ενημερώνεται μόνο του", desc: "Οι συναντήσεις αλλάζουν αυτόματα τα στάδια. Οι ενέργειες γίνονται εργασίες. Οι προβλέψεις συγκεντρώνονται στην προβολή του υπευθύνου, χωρίς καμία χειροκίνητη αλλαγή." },
      ]}
      deepDives={[
        {
          eyebrow: "Καταγραφή",
          title: "Κάθε χειραψία γίνεται",
          italic: "μια καταχωρισμένη επαφή.",
          description: "Αγγίξτε την κάρτα NFC σας σε ένα περίπτερο, σαρώστε μια χάρτινη κάρτα μέσα σε ένα Uber ή προωθήστε μια πρόσκληση συνάντησης — το Banana καταγράφει τον υποψήφιο πελάτη και τον αναθέτει στον σωστό πωλητή, pipeline και στάδιο.",
          points: [
            "Άγγιγμα NFC σε οποιοδήποτε iPhone ή Android",
            "Σάρωση επαγγελματικών καρτών με AI (OCR + εμπλουτισμός)",
            "Κυκλική ανάθεση & δρομολόγηση ανά περιοχή",
            "Αυτόματος εντοπισμός διπλοεγγραφών στους υπάρχοντες λογαριασμούς",
          ],
          image: capture,
        },
        {
          eyebrow: "Follow-up",
          title: "Το AI γράφει,",
          italic: "εσείς κλείνετε συμφωνίες.",
          description: "Μετά από κάθε συνάντηση, το Banana γράφει μια εξατομικευμένη σύνοψη, προτείνει επόμενα βήματα και προγραμματίζει μια ακολουθία follow-up που προσαρμόζεται ανάλογα με το αν ο υποψήφιος πελάτης ανοίγει, πατάει ή κλείνει ραντεβού.",
          points: [
            "Email σύνοψης από απομαγνητοφωνήσεις συναντήσεων",
            "Ακολουθίες που διακλαδίζονται ανάλογα με την αλληλεπίδραση",
            "Δοκιμές A/B σε θέματα email ανά προφίλ πελάτη",
            "Αποστολή με ένα κλικ από τα εισερχόμενα ή το CRM",
          ],
          image: follow,
          reverse: true,
        },
        {
          eyebrow: "Προβλέψεις",
          title: "Ένα pipeline που μπορείτε",
          italic: "πραγματικά να εμπιστευτείτε.",
          description: "Τα στάδια προχωρούν όταν υπάρχει πραγματική δραστηριότητα — απαντήσεις σε email, κρατήσεις συναντήσεων, προβολές συμβάσεων. Τέλος στην πίεση του τέλους τριμήνου για ενημέρωση πεδίων CRM που κανείς δεν εμπιστεύεται.",
          points: [
            "Αυτόματη εξέλιξη σταδίων βάσει πραγματικής δραστηριότητας",
            "Σταθμισμένες προβλέψεις σε πολλά νομίσματα",
            "Ειδοποιήσεις καθυστέρησης & κινδύνου για στάσιμες συμφωνίες",
            "Πίνακες ελέγχου υπευθύνων & κάρτες απόδοσης πωλητών",
          ],
          image: pipeline,
        },
      ]}
      howItWorks={[
        { title: "Στείλτε κάρτες στην ομάδα σας", desc: "Κάρτες NFC με την εταιρική σας ταυτότητα, με παγκόσμια παράδοση σε 5–7 ημέρες. Ο παραλήπτης δεν χρειάζεται εφαρμογή." },
        { title: "Συνδέστε εισερχόμενα & ημερολόγιο", desc: "Gmail, Outlook, Google & iCloud συγχρονίζονται με δύο κλικ. Τα υπάρχοντα δεδομένα CRM εισάγονται σε λίγα λεπτά." },
        { title: "Καταγράψτε & αφήστε το AI να δουλέψει", desc: "Οι πωλητές αγγίζουν την κάρτα τους. Οι συναντήσεις αποκτούν σημειώσεις. Τα προσχέδια εμφανίζονται στα εξερχόμενα. Το pipeline προχωρά μόνο του." },
        { title: "Καθοδήγηση με πραγματικά δεδομένα", desc: "Οι υπεύθυνοι βλέπουν ποιος κάνει follow-up, ποιος όχι και ποιες συμφωνίες καθυστερούν — σε πραγματικό χρόνο." },
      ]}
      capabilities={[
        "Επαγγελματικές κάρτες NFC με εταιρική ταυτότητα",
        "Βοηθός σημειώσεων AI (Zoom/Meet/Teams)",
        "Αυτόματη σύνταξη email follow-up",
        "Pipeline με σταθμισμένες προβλέψεις",
        "Κυκλική ανάθεση υποψήφιων πελατών",
        "Συγχρονισμός Salesforce & HubSpot",
        "Αναλύσεις ομάδας & κάρτες απόδοσης πωλητών",
        "SSO, SCIM & πρόσβαση βάσει ρόλων",
      ]}
      faq={[
        { q: "Πώς καταγράφει το Banana υποψήφιους πελάτες σε εκδηλώσεις;", a: "Οι πωλητές αγγίζουν με την επαγγελματική τους κάρτα NFC οποιοδήποτε τηλέφωνο — χωρίς εφαρμογή. Ο υποψήφιος πελάτης βλέπει ένα εταιρικό προφίλ και μπορεί να αποθηκεύσει την επαφή ή να μοιραστεί τα δικά του στοιχεία. Αν τα μοιραστεί, προστίθεται στο pipeline με όνομα, τίτλο, εταιρεία, LinkedIn και απαντήσεις σε προσαρμοσμένες φόρμες. Για χάρτινες κάρτες, ο σαρωτής AI εξάγει τα ίδια πεδία από μια φωτογραφία σε δευτερόλεπτα." },
        { q: "Τι ακριβώς γράφει το AI στο follow-up;", a: "Μετά τη συνάντηση, το AI συνοψίζει τη συζήτηση, τις δυσκολίες που ανέφερε ο υποψήφιος πελάτης και τα συμφωνημένα επόμενα βήματα. Έπειτα συντάσσει ένα εξατομικευμένο email σύνοψης με το ύφος σας και προγραμματίζει μια ακολουθία πέντε επαφών, ανάλογα με το στάδιο της συμφωνίας. Μπορείτε να αλλάξετε τα πάντα πριν από την αποστολή ή να επιλέξετε αυτόματη αποστολή με χρονική καθυστέρηση." },
        { q: "Συγχρονίζεται με Salesforce ή HubSpot;", a: "Ναι — αμφίδρομος συγχρονισμός με Salesforce, HubSpot και Pipedrive. Επαφές, λογαριασμοί, ευκαιρίες και δραστηριότητες αντιστοιχίζονται και προς τις δύο κατευθύνσεις. Κρατήστε το υπάρχον CRM ως κύρια πηγή δεδομένων και χρησιμοποιήστε το Banana για καταγραφή και AI ή δουλέψτε αποκλειστικά με αυτό." },
        { q: "Μπορούν οι υπεύθυνοι να δουν τι κάνει κάθε πωλητής;", a: "Ναι. Ο πίνακας ελέγχου εμφανίζει κάθε συνάντηση, follow-up και αλλαγή σταδίου ανά πωλητή, με προβλέψεις και δείκτες κατάστασης pipeline για όλη την ομάδα. Οι προτάσεις καθοδήγησης αναδεικνύουν συμφωνίες χωρίς δραστηριότητα για πάνω από 7 ημέρες ή χωρίς τα απαιτούμενα επόμενα βήματα." },
        { q: "Πόσο γρήγορα ξεκινά μια ομάδα 50 ατόμων;", a: "Οι κάρτες αποστέλλονται σε λιγότερο από μία εβδομάδα. Η ρύθμιση — εισερχόμενα, ημερολόγιο, συγχρονισμός CRM, SSO — απαιτεί περίπου μία ώρα από τον διαχειριστή. Οι πωλητές συνήθως αρχίζουν να καταγράφουν υποψήφιους πελάτες την ημέρα που παραλαμβάνουν την κάρτα τους. Οι περισσότερες ομάδες βλέπουν το πρώτο follow-up από AI μέσα σε 24 ώρες." },
      ]}
      outro={{
        title: "Δώστε πίσω στους πωλητές σας",
        italic: "τον χρόνο τους.",
        description: "Μην πληρώνετε έμπειρους πωλητές για να καταχωρίζουν δεδομένα. Το Banana καταγράφει, συντάσσει και ενημερώνει — ώστε η ομάδα σας να πουλάει.",
      }}
      afterFaq={<UseCases tag="Πωλήσεις" />}
    />
  );
}

function UseCases({ tag }: { tag: string }) {
  const items = [
    { t: "Πωλήσεις πεδίου & υπεύθυνοι περιοχής", d: "Κοινοποίηση με άγγιγμα στις επισκέψεις, άμεση καταχώριση στο CRM και follow-up που λαμβάνει υπόψη τον χρόνο μετακίνησης.", to: "/platform/scout" },
    { t: "Εσωτερικές πωλήσεις & SDR", d: "Συνδέστε εισερχόμενα και ημερολόγιο, αφήστε το AI να γράψει εξατομικευμένες εισαγωγές και κλείστε συναντήσεις.", to: "/ai/email-writer" },
    { t: "Ομάδες εκδηλώσεων & συνεδρίων", d: "Καταγράψτε εκατοντάδες διαπιστεύσεις σε μία ημέρα, αναθέστε ανά περιοχή και κάντε follow-up πριν από την πτήση επιστροφής.", to: "/platform/events" },
    { t: "Υπεύθυνοι λογαριασμών", d: "Αυτόματες συνόψεις διερευνητικών κλήσεων, δημιουργία προτάσεων και συμβάσεων, προβλέψεις με σιγουριά.", to: "/el/platform/contracts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Εφαρμογές: {tag}</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Κάθε ρόλος στην ομάδα σας, <em className="italic">με πλήρη κάλυψη.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => (
            <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
              <h3 className="text-lg font-bold">{i.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                Εξερευνήστε <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}